import React, { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './command';
import { Button } from './button';
import { Check, ChevronsUpDown } from 'lucide-react';

/**
 * Searchable combobox for product sizes.
 * Props:
 *  - value: current selected size string
 *  - onValueChange: callback(newSize)
 *  - groupedProducts: { "thickness": [{ size: "20x25x1" }, ...], ... } (optional)
 *  - flatProducts: [{ size: "20x25x1" }, ...] (optional, used if groupedProducts not provided)
 *  - placeholder: placeholder text
 *  - className: additional classes for trigger
 *  - data-testid: test id for trigger button
 */
export const SizeCombobox = ({ value, onValueChange, groupedProducts, flatProducts, placeholder = "Search size...", className = "", ...props }) => {
  const [open, setOpen] = useState(false);

  // Build groups from flat products if needed
  const hasGrouped = groupedProducts && Object.keys(groupedProducts).length > 0;
  const groups = hasGrouped ? groupedProducts : flatProducts ? { 'All': flatProducts } : {};

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`w-full justify-between h-10 font-normal ${!value ? 'text-muted-foreground' : ''} ${className}`}
          data-testid={props['data-testid']}
        >
          {value || placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Type size to search..." />
          <CommandList className="max-h-[250px]">
            <CommandEmpty>No size found.</CommandEmpty>
            {Object.entries(groups).map(([groupName, prods]) => (
              <CommandGroup key={groupName} heading={hasGrouped && /^\d+$/.test(groupName) ? `${groupName}" Filters` : hasGrouped ? groupName : undefined}>
                {prods.map(p => (
                  <CommandItem
                    key={p.size}
                    value={p.size}
                    onSelect={() => {
                      onValueChange(p.size);
                      setOpen(false);
                    }}
                  >
                    <Check className={`mr-2 h-4 w-4 ${value === p.size ? 'opacity-100' : 'opacity-0'}`} />
                    {p.size}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default SizeCombobox;
